import { useMemo } from "react"
import { Boost } from "@mezo/api"
import {
  Block,
  FlexGrid,
  FlexGridItem,
  HeadingSmall,
  Mats,
  ParagraphSmall,
  useStyletron,
} from "@mezo-org/mezo-clay"

import {
  RootState,
  selectCurrentLockForDeposit,
  selectWithdrawalById,
  useDappSelector,
} from "#/redux-state"
import { IS_MEZO_MAINNET, LOCK_TIMES } from "#/shared/constants"

import DepositAutoMigrationState from "./DepositAutoMigrationState"
import DepositBoxHeaderButton from "./DepositBoxHeaderButton"
import DepositStablecoinBoost from "./DepositStablecoinBoost"

type DepositBoxHeaderProps = {
  depositId: number | null
  id: string
  unlockTime: string | null
  lockDuration: number
  dailyMats: string
  dailyBoostMats: Boost[]
  onBoostButtonClick: () => void
}

export default function DepositBoxHeader({
  depositId,
  id,
  unlockTime,
  lockDuration,
  dailyMats,
  dailyBoostMats,
  onBoostButtonClick,
}: DepositBoxHeaderProps) {
  const [css, theme] = useStyletron()

  const currentLock = useDappSelector((state: RootState) =>
    selectCurrentLockForDeposit(state, id),
  )

  const existingWithdrawal = useDappSelector((state: RootState) =>
    selectWithdrawalById(state, depositId),
  )

  const enableTokenRelock = useMemo(
    () =>
      !existingWithdrawal &&
      !!depositId &&
      (!unlockTime || lockDuration < LOCK_TIMES[0].period),
    [depositId, existingWithdrawal, lockDuration, unlockTime],
  )

  if (!currentLock) return null

  return (
    <>
      {id && <DepositAutoMigrationState depositUuid={id} />}
      <Block
        backgroundColor={theme.colors.white}
        padding={`${theme.sizing.scale800} ${theme.sizing.scale800} ${theme.sizing.scale300}`}
      >
        <FlexGrid flexGridColumnCount={2} flexGridColumnGap="scale400">
          <FlexGridItem>
            <Block>
              <Block
                className={css({
                  display: "flex",
                  alignItems: "center",
                  gap: theme.sizing.scale300,
                  marginBottom: theme.sizing.scale100,
                })}
              >
                <HeadingSmall>{unlockTime ? dailyMats : 0}</HeadingSmall>
                <Mats size={16} />
                <ParagraphSmall color={theme.colors.contentSecondary}>
                  per day
                </ParagraphSmall>
              </Block>
              <DepositStablecoinBoost dailyBoostMats={dailyBoostMats} />
            </Block>
          </FlexGridItem>
          <FlexGridItem
            overrides={{
              Block: { style: () => ({ flexGrow: 0, width: "auto" }) },
            }}
          >
            {!IS_MEZO_MAINNET && (
              <DepositBoxHeaderButton
                enableTokenRelock={enableTokenRelock}
                unlockTime={unlockTime}
                onClick={onBoostButtonClick}
              />
            )}
          </FlexGridItem>
        </FlexGrid>
      </Block>
    </>
  )
}
